// Polyfills `AbortSignal.any`/`AbortSignal.timeout` on engines that lack them
// (Safari < 17.4, Chrome/Edge < 116). Each returns a real `controller.signal`,
// so native `fetch` accepts the result as a genuine AbortSignal.

type AbortSignalStatics = {
  any?: (signals: AbortSignal[]) => AbortSignal;
  timeout?: (ms: number) => AbortSignal;
};

export function abortSignalAny(signals: AbortSignal[]): AbortSignal {
  const controller = new AbortController();
  const cleanups: Array<() => void> = [];
  const finish = (reason: unknown) => {
    controller.abort(reason);
    cleanups.forEach((fn) => fn());
  };

  for (const source of signals) {
    if (source.aborted) {
      finish(source.reason);
      break;
    }
    const handler = () => finish(source.reason);
    source.addEventListener('abort', handler);
    cleanups.push(() => source.removeEventListener('abort', handler));
  }

  return controller.signal;
}

export function abortSignalTimeout(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => {
    controller.abort(new DOMException(`signal timed out after ${ms} ms`, 'TimeoutError'));
  }, ms);
  return controller.signal;
}

// Only patches missing statics, so native implementations are never overwritten.
export function installAbortSignalPolyfill(): void {
  if (typeof AbortSignal === 'undefined') return;

  const statics = AbortSignal as unknown as AbortSignalStatics;
  if (typeof statics.any !== 'function') statics.any = abortSignalAny;
  if (typeof statics.timeout !== 'function') statics.timeout = abortSignalTimeout;
}
